import { useCallback, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { useEffect } from "react";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import Delete from "@mui/icons-material/Delete";
import Alert from "@mui/material/Alert";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import { useConfirm } from "material-ui-confirm";
import ProgramWeekDetails from "./ProgramWeekDetails";
import { standardDateFormat } from "../../actprep-ui/utils/calendar";
import api from "../../actprep-ui/utils/api";
import LoadingButton from "@mui/lab/LoadingButton";
import StudentsToProgramForm from "../StudentsToProgramForm";
import Remove from "@mui/icons-material/Remove";
import Add from "@mui/icons-material/Add";

function getProgramWeekListFromStudentProgram(studentToProgram) {
  const programStartDate = dayjs(studentToProgram.start_date).toDate();
  return studentToProgram.program.program_weeks.map((programWeek, i) => {
    const startDate = new Date(programStartDate);
    startDate.setDate(programStartDate.getDate() + parseInt(i * 7));
    programWeek.start_date = startDate;
    return programWeek;
  });
}

export default function GenerateOneStudentsDataForm({
  onDataGenerated,
  student,
}) {
  const confirm = useConfirm();
  const { data: studentToPrograms, refetch: refetchPrograms } = useGet(
    apiUrls.admin.studentToProgram(),
    {
      params: {
        search_params: {
          student__uuid: student.uuid,
        },
        keep_related: true,
        include_program_weeks: true,
      },
      enabled: !!student,
      staleTime: 0,
    }
  );
  const selectedRowIdsRef = useRef([]);

  const [enabledPrograms, setEnabledPrograms] = useState({});

  const { data: studentWeeks, refetch: refetchStudentWeeks } = useGet(
    apiUrls.admin.studentWeeks(),
    {
      params: {
        search_params: {
          student__uuid: student.uuid,
        },
        keep_related: true,
      },
      enabled: !!student,
      staleTime: 0,
    }
  );

  const [isLoading, setIsLoading] = useState(false);

  const [weeks, setWeeks] = useState([]);
  const gridApiRef = useGridApiRef();
  const [formData, setFormData] = useState({
    program_week: null,
    start_date: "",
  });
  const [error, setError] = useState(null);
  const {
    getDisclosureProps: getAddWeekDiscProps,
    onOpen: onOpenAddWeekDisc,
    onClose: onCloseAddWeekDisc,
  } = useDisclosure();

  const {
    getButtonProps: getAddToProgramButtonProps,
    getDisclosureProps: getAddToProgramDiscProps,
    onClose: closeAddToProgramDialog,
  } = useDisclosure();

  const {
    getButtonProps: getRemoveFromProgramButtonProps,
    getDisclosureProps: getRemoveFromProgramDiscProps,
    onClose: closeRemoveFromProgramDialog,
  } = useDisclosure();

  const formatWeekRows = (weeks) => {
    weeks.sort((a, b) => a.start_date - b.start_date);
    return weeks.map((obj, i) => ({ ...obj, id: i + 1 }));
  };

  const updateWeekRow = (rowId) => (data) => {
    setWeeks((currentWeeks) => {
      const newWeeks = [...currentWeeks];
      newWeeks[rowId - 1] = {
        ...newWeeks[rowId - 1],
        ...data,
      };
      return formatWeekRows(newWeeks);
    });
  };

  const handleSelectionModelChange = (selectionModel) => {
    const selectedIds =
      selectionModel.type === "exclude"
        ? weeks
            .filter((week) => !selectionModel.ids.has(week.id))
            .map((week) => week.id)
        : [...selectionModel.ids];

    selectedRowIdsRef.current = selectedIds;
  };

  const validateStartDate = (startDate, rowId = -1) => {
    for (let week of weeks) {
      if (rowId == week.id) {
        continue;
      }

      const diff = Math.abs(
        dayjs(week.start_date).diff(dayjs(startDate), "day")
      );
      if (diff < 7) {
        return false;
      }
    }
    return true;
  };

  const handleAddWeek = (event) => {
    event.preventDefault();
    if (!validateStartDate(formData.start_date)) {
      setError("The new week cannot be within 7 days of an existing week.");
      return;
    }
    setError(null);
    const newWeek = {
      start_date: formData.start_date,
      program_week: {
        uuid: formData.program_week.uuid,
        title: formData.program_week.label,
      },
    };
    for (let week of studentWeeks.data) {
      if (
        newWeek.start_date.toLocaleDateString() ==
        week.start_date.toLocaleDateString()
      ) {
        newWeek.week = week;
        break;
      }
    }
    setWeeks((weeks) => formatWeekRows([...weeks, newWeek]));
    onCloseAddWeekDisc();
    showSuccessSnackbar("Week added!");
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    const data = weeks.map((week) => {
      const copy = { ...week };
      delete copy.id;
      copy.start_date = standardDateFormat(week.start_date);
      return copy;
    });
    const response = await api.post(apiUrls.admin.generateProgramData(), {
      student_uuids: [student.uuid],
      weeks: data,
    });

    const { results } = response.data;
    const studentResult = results[0];
    setIsLoading(false);
    if (studentResult.success) {
      refetchStudentWeeks();
      onDataGenerated(false);
    } else {
      confirm({
        title: "Week Generation Failed",
        description: studentResult.reason,
        hideCancelButton: true,
        confirmationText: "Ok",
      });
    }
  };

  const handleReset = () => {
    confirm({
      description: `Are you sure you want to reset your changes?`,
      confirmationText: "Reset",
    }).then(() => {
      mergeAndLoadWeeks();
    });
  };

  const mergeAndLoadWeeks = useCallback(() => {
    let startDateToProgramWeekMap = {};
    for (let program of studentToPrograms.data) {
      if (!enabledPrograms[program.uuid]) {
        continue;
      }

      for (let programWeek of getProgramWeekListFromStudentProgram(program)) {
        startDateToProgramWeekMap[programWeek.start_date] = {
          ...startDateToProgramWeekMap[programWeek.start_date],
          program: `${program.program.title} (${program.start_date})`,
          program_week: programWeek,
          start_date: programWeek.start_date,
        };
      }
    }
    for (let week of studentWeeks.data) {
      week.start_date = dayjs(week.start_date).toDate();
      startDateToProgramWeekMap[week.start_date] = {
        ...startDateToProgramWeekMap[week.start_date],
        week: week,
        start_date: week.start_date,
      };
    }

    setWeeks(formatWeekRows(Object.values(startDateToProgramWeekMap)));
  }, [studentToPrograms?.data, studentWeeks?.data, enabledPrograms]);

  const handleRemoveSelectedWeeks = async () => {
    if (!selectedRowIdsRef.current.length) {
      return;
    }
    const isConfirmed = await new Promise((resolve) => {
      confirm({
        description: `Please confirm that you want to remove ${selectedRowIdsRef.current.length} weeks`,
        confirmationText: "Remove",
      })
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });
    if (!isConfirmed) {
      return;
    }

    setWeeks((currentWeeks) => {
      return currentWeeks.filter(
        (week) => !selectedRowIdsRef.current.includes(week.id)
      );
    });
    showSuccessSnackbar(`${selectedRowIdsRef.current.length} weeks removed`);
  };

  useEffect(() => {
    if (!(studentToPrograms && student && studentWeeks)) {
      return;
    }

    mergeAndLoadWeeks();
  }, [
    studentToPrograms,
    studentWeeks,
    student,
    mergeAndLoadWeeks,
    enabledPrograms,
  ]);

  const handleRemoveWeek = (row, rowId) => {
    confirm({
      description: `Please confirm that you want to remove the week starting on ${row.start_date.toLocaleDateString()}`,
      confirmationText: "Remove",
    }).then(() => {
      setWeeks((currentWeeks) => {
        const copy = [...currentWeeks];
        copy.splice(
          copy.findIndex((week) => week.id == rowId),
          1
        );
        return copy;
      });
    });
  };

  const columns = [
    {
      field: "program_week",
      headerName: "New Week",
      width: 200,
      valueGetter: (val) => val?.title,
    },
    {
      field: "program",
      headerName: "Program",
      width: 200,
    },
    {
      field: "start_date",
      headerName: "Start Date",
      valueGetter: (val) => val.toLocaleDateString(),
    },
    {
      field: "week",
      headerName: "Current Week",
      width: 200,
      valueGetter: (val) => val?.verbose_name,
    },
    {
      field: "week.stats_count",
      headerName: "Stat Collections",
      width: 150,
      valueGetter: (val, row) => row.week?.stats_count,
    },
    {
      field: "actions",
      type: "actions",
      width: 25,
      getActions: ({ row, id }) => {
        return [
          <Tooltip title="Remove Week" key={1}>
            <GridActionsCellItem
              icon={<Delete color="primary" />}
              label="Remove Week"
              onClick={() => handleRemoveWeek(row, id)}
            />
          </Tooltip>,
        ];
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          width: "100%",
        }}
      >
        <Typography variant="overline" fontWeight={"bold"} fontSize={"large"}>
          {student.first_name} {student.last_name}
        </Typography>
        <Box component={Paper} p={1} my={1}>
          <Stack
            direction={"row"}
            flexWrap={"wrap"}
            justifyContent={"flex-start"}
            spacing={1}
          >
            <Typography variant="h6">Assigned Programs</Typography>{" "}
            <Box>
              <Chip
                icon={<Add />}
                label="Add"
                {...getAddToProgramButtonProps()}
                sx={{ marginRight: ".5rem" }}
              />
              <Chip
                icon={<Remove />}
                label="Remove"
                {...getRemoveFromProgramButtonProps()}
              />
            </Box>
          </Stack>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            mx={2}
            mt={2}
          >
            <Typography variant="body1" fontWeight={"bold"}>
              Program
            </Typography>
            <Typography variant="body1" fontWeight={"bold"}>
              Enabled
            </Typography>
          </Stack>
          <List dense>
            {studentToPrograms?.data.map((studentToProgram, i) => (
              <ListItem
                key={i}
                disablePadding
                secondaryAction={
                  <Checkbox
                    edge="end"
                    color="error"
                    onChange={(_, checked) =>
                      setEnabledPrograms((enabledPrograms) => ({
                        ...enabledPrograms,
                        [studentToProgram.uuid]: checked,
                      }))
                    }
                    checked={enabledPrograms[studentToProgram.uuid] || false}
                  />
                }
              >
                <ListItemButton
                  onClick={() =>
                    setEnabledPrograms((enabledPrograms) => ({
                      ...enabledPrograms,
                      [studentToProgram.uuid]:
                        !enabledPrograms[studentToProgram.uuid],
                    }))
                  }
                >
                  <ListItemText
                    primary={studentToProgram.program.title}
                    secondary={`Starting ${dayjs(studentToProgram.start_date)
                      .toDate()
                      .toLocaleDateString()}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box component={Paper} elevation={2} p={1}>
          <Typography variant="h6">Student Weeks</Typography>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            flexWrap="wrap"
            gap={0.25}
            my={1}
          >
            <ButtonGroup variant="standard">
              <Button onClick={onOpenAddWeekDisc} startIcon={<Add />}>
                Add Week
              </Button>
              <Button
                onClick={handleRemoveSelectedWeeks}
                startIcon={<Delete />}
              >
                Remove Selected Weeks
              </Button>
            </ButtonGroup>
            <ButtonGroup variant="contained" disableElevation>
              <LoadingButton
                size="small"
                onClick={handleGenerate}
                loading={isLoading}
              >
                Save Changes
              </LoadingButton>
              <Button
                size="small"
                onClick={handleReset}
                color="error"
                variant="contained"
              >
                Reset
              </Button>
            </ButtonGroup>
          </Box>
          <Box>
            <DataGridPro
              apiRef={gridApiRef}
              rows={weeks}
              columns={columns}
              autoHeight
              getDetailPanelContent={({ row, id }) => (
                <ProgramWeekDetails
                  row={row}
                  updateWeek={updateWeekRow(id)}
                  validateStartDate={validateStartDate}
                />
              )}
              getDetailPanelHeight={() => "auto"}
              onRowClick={({ id }) => gridApiRef.current.toggleDetailPanel(id)}
              initialState={{ pinnedColumns: { right: ["actions"] } }}
              disableRowSelectionOnClick
              checkboxSelection
              onRowSelectionModelChange={handleSelectionModelChange}
            />
          </Box>
        </Box>
      </Box>
      <Dialog {...getAddToProgramDiscProps()} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add {student.first_name} {student.last_name} to Program
        </DialogTitle>
        <DialogContent>
          <StudentsToProgramForm
            students={[student]}
            onActionComplete={() => {
              closeAddToProgramDialog();
              refetchPrograms();
            }}
            action="add"
          />
        </DialogContent>
      </Dialog>
      <Dialog {...getRemoveFromProgramDiscProps()} maxWidth="sm" fullWidth>
        <DialogTitle>
          Remove {student.first_name} {student.last_name} from Program
        </DialogTitle>
        <DialogContent>
          <StudentsToProgramForm
            students={[student]}
            onActionComplete={() => {
              closeRemoveFromProgramDialog();
              refetchPrograms();
            }}
            action="remove"
          />
        </DialogContent>
      </Dialog>
      <Dialog
        {...getAddWeekDiscProps()}
        component={"form"}
        onSubmit={handleAddWeek}
        maxWidth="sm"
      >
        <DialogTitle>Add Week</DialogTitle>
        <DialogContent>
          <Grid container gap={4}>
            <Grid size={12}>
              <ModelAutoComplete
                modelName={"ProgramWeek"}
                label={"New Program Week"}
                value={formData.program_week}
                setValue={(newValue) =>
                  setFormData((formData) => ({
                    ...formData,
                    program_week: newValue,
                  }))
                }
              />
            </Grid>
            <Grid size={12}>
              <DatePicker
                label="Start Date"
                slotProps={{
                  textField: { variant: "standard", fullWidth: true },
                }}
                value={formData.start_date ? dayjs(formData.start_date) : null}
                onChange={(newValue) =>
                  setFormData((formData) => ({
                    ...formData,
                    start_date: newValue?.toDate(),
                  }))
                }
              />
            </Grid>
          </Grid>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseAddWeekDisc}>Cancel</Button>
          <Button type="submit">Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
